import { useMemo, useEffect } from "react";
import { Box, TextField, Autocomplete } from "@mui/material";
import useLocations from "../hooks/useLocations";

export default function LocationsSelector({ values, setValues }) {
  const { countries, regions, subregions } = useLocations();

  const metafieldCountry = values?.metafields?.country;
  const metafieldRegion = values?.metafields?.region;
  const metafieldSubregion = values?.metafields?.subregion;

  const selectedCountry = useMemo(() => {
    if (!metafieldCountry) return null;
    return countries.find((c) => c.name === metafieldCountry) || null;
  }, [countries, metafieldCountry]);

  const selectedRegion = useMemo(() => {
    if (!metafieldRegion || !selectedCountry) return null;
    return regions.find(
      (r) => r.name === metafieldRegion && r.parentCountryId === selectedCountry?.id
    ) || null;
  }, [regions, metafieldRegion, selectedCountry]);

  const selectedSubregion = useMemo(() => {
    if (!metafieldSubregion || !selectedRegion) return null;
    return subregions.find(
      (s) => s.name === metafieldSubregion && s.parentRegionId === selectedRegion?.id
    ) || null;
  }, [subregions, metafieldSubregion, selectedRegion]);

  const filteredRegions = useMemo(() => {
    if (!selectedCountry) return [];
    return regions.filter((r) => r.parentCountryId === selectedCountry.id);
  }, [regions, selectedCountry]);

  const filteredSubregions = useMemo(() => {
    if (!selectedRegion) return [];
    return subregions.filter((s) => s.parentRegionId === selectedRegion.id);
  }, [subregions, selectedRegion]);

  // Validate country
  useEffect(() => {
    if (!countries.length || !metafieldCountry) return;
    if (!countries.some((c) => c.name === metafieldCountry))
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), country: "", region: "", subregion: "" },
      }));
  }, [countries, metafieldCountry, setValues]);

  // Validate region
  useEffect(() => {
    if (!regions.length || !metafieldRegion) return;
    if (!filteredRegions.some((r) => r.name === metafieldRegion))
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), region: "", subregion: "" },
      }));
  }, [filteredRegions, metafieldRegion, regions, setValues]);

  // Validate subregion
  useEffect(() => {
    if (!subregions.length || !metafieldSubregion) return;
    if (!filteredSubregions.some((s) => s.name === metafieldSubregion))
      setValues((prev) => ({
        ...prev,
        metafields: { ...(prev.metafields || {}), subregion: "" },
      }));
  }, [filteredSubregions, metafieldSubregion, subregions, setValues]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 2,
      }}
    >
      <Autocomplete
        options={countries}
        getOptionLabel={(option) => option.name}
        value={selectedCountry}
        onChange={(e, newValue) =>
          setValues((prev) => ({
            ...prev,
            metafields: {
              ...(prev.metafields || {}),
              country: newValue?.name || "",
              region: "",
              subregion: "",
            },
          }))
        }
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={(params) => (
          <TextField {...params} label="Country" fullWidth size="small" />
        )}
        size="small"
        sx={{ width: "100%" }}
      />

      <Autocomplete
        options={filteredRegions}
        getOptionLabel={(option) => option.name}
        value={selectedRegion}
        onChange={(e, newValue) =>
          setValues((prev) => ({
            ...prev,
            metafields: {
              ...(prev.metafields || {}),
              region: newValue?.name || "",
              subregion: "",
            },
          }))
        }
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={(params) => (
          <TextField {...params} label="Region" fullWidth size="small" />
        )}
        size="small"
        disabled={!metafieldCountry || filteredRegions.length === 0}
        sx={{ width: "100%" }}
      />

      <Autocomplete
        options={filteredSubregions}
        getOptionLabel={(option) => option.name}
        value={selectedSubregion}
        onChange={(e, newValue) =>
          setValues((prev) => ({
            ...prev,
            metafields: {
              ...(prev.metafields || {}),
              subregion: newValue?.name || "",
            },
          }))
        }
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={(params) => (
          <TextField {...params} label="Subregion" fullWidth size="small" />
        )}
        size="small"
        disabled={!metafieldRegion || filteredSubregions.length === 0}
        sx={{ width: "100%" }}
      />
    </Box>
  );
}